import { Building2, Check, Gamepad2, HandCoins, MessageCircle, Sparkles, Wrench } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { Magnetic } from "./Magnetic";
import { DEPOSIT_PLAN, PHONE_DISPLAY, PLANS } from "@/data/content";

const ICONS = { Building2, Gamepad2, Wrench };

const whatsappFor = (plan) =>
  `https://wa.me/553196335635?text=${encodeURIComponent(`Olá Luiz! Quero fechar o plano ${plan.name} e combinar o valor com você.`)}`;

const PlanCard = ({ plan, index }) => {
  const Icon = ICONS[plan.icon];
  return (
    <Reveal delay={index * 0.12} className="h-full">
      <article
        data-testid={`plan-card-${plan.lookup_key}`}
        className={`card-glow group relative flex h-full flex-col overflow-hidden rounded-2xl p-8 ${
          plan.featured
            ? "border border-[#E50914]/60 bg-gradient-to-b from-[#1a0508] to-[#101014] shadow-[0_0_60px_rgba(229,9,20,0.15)]"
            : "border border-[#22222A] bg-[#101014]"
        }`}
      >
        {plan.featured && (
          <span
            data-testid="plan-featured-badge"
            className="absolute right-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-[#E50914] px-3 py-1 font-mono-code text-[10px] font-bold uppercase tracking-widest text-white"
          >
            <Sparkles className="h-3 w-3" />
            Mais pedido
          </span>
        )}
        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#E50914]/30 bg-[#E50914]/10">
          <Icon className="h-6 w-6 text-[#E50914]" />
        </div>
        <h3 className="mt-6 font-display text-xl font-bold text-zinc-100 sm:text-2xl">{plan.name}</h3>
        <p className="mt-2 text-sm text-zinc-500">{plan.tagline}</p>
        <div className="mt-6 flex items-baseline gap-2">
          <span data-testid={`plan-price-${plan.lookup_key}`} className="font-display text-2xl font-extrabold tracking-tight text-zinc-100 sm:text-3xl">
            {plan.price}
          </span>
        </div>
        <span className="mt-1 font-mono-code text-[10px] uppercase tracking-[0.2em] text-zinc-500">{plan.priceNote}</span>

        <ul className="mt-8 flex-1 space-y-3 border-t border-[#1A1A20] pt-6">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E50914]" />
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <a
            data-testid={`plan-whatsapp-btn-${plan.lookup_key}`}
            href={whatsappFor(plan)}
            target="_blank"
            rel="noopener noreferrer"
            className={`btn-red-glow btn-shine flex h-12 w-full items-center justify-center gap-2 rounded-full font-mono-code text-xs font-bold uppercase tracking-widest text-white ${
              plan.featured ? "bg-[#E50914]" : "bg-[#E50914]/90 hover:bg-[#E50914]"
            }`}
          >
            <MessageCircle className="h-4 w-4" />
            Combinar pelo WhatsApp
          </a>
        </div>
      </article>
    </Reveal>
  );
};

export const Plans = () => {
  const depositWhatsapp = `https://wa.me/553196335635?text=${encodeURIComponent(
    "Olá Luiz! Quero garantir minha vaga na agenda e combinar os detalhes do projeto."
  )}`;

  return (
    <section id="planos" className="relative border-t border-[#1A1A20] bg-[#0A0A0C]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full opacity-[0.12] blur-[140px]"
        style={{ background: "radial-gradient(circle, #E50914 0%, transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading number="06" label="Planos" title="Escolha seu plano e" accent="combine o valor comigo." />
          <Reveal delay={0.15}>
            <div className="flex max-w-md flex-col gap-3">
              <p className="text-sm leading-relaxed text-zinc-500 sm:text-base">
                Cada projeto é único — por isso fechamos o valor direto no WhatsApp, de acordo com o que você precisa.
                Me chame, conte sua ideia e eu te passo a proposta sob medida no mesmo dia.
              </p>
              <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#22222A] bg-[#101014] px-3.5 py-1.5 font-mono-code text-[10px] uppercase tracking-widest text-zinc-500">
                <MessageCircle className="h-3.5 w-3.5 text-[#E50914]" />
                WhatsApp · {PHONE_DISPLAY}
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="plans-grid">
          {PLANS.map((plan, i) => (
            <PlanCard key={plan.lookup_key} plan={plan} index={i} />
          ))}
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="plan-deposit-card"
            className="card-glow mt-6 flex flex-col gap-6 rounded-2xl border border-[#22222A] bg-[#101014] p-8 md:flex-row md:items-center md:justify-between"
          >
            <div className="flex items-start gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#E50914]/30 bg-[#E50914]/10">
                <HandCoins className="h-6 w-6 text-[#E50914]" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-xl font-bold text-zinc-100">{DEPOSIT_PLAN.name}</h3>
                </div>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-500">{DEPOSIT_PLAN.tagline}</p>
              </div>
            </div>
            <Magnetic strength={0.2}>
              <a
                data-testid="plan-whatsapp-btn-sinal_projeto"
                href={depositWhatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-red-glow btn-shine flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-[#E50914] px-8 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Falar no WhatsApp
              </a>
            </Magnetic>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-8 text-center font-mono-code text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            Projeto fora desses planos? Me chame no WhatsApp para um orçamento sob medida.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
